import { Link } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import { categories, movieStats } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { useSeo } from '@/hooks/useSeo';

const contact = 'contact@jkapf-demo.com';

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 日韩成人电影',
    description:
      '了解日韩成人电影的站点定位、内容范围、使用说明与免责声明。本站为面向成年观众的影视资料索引，不提供播放、下载或交易服务。',
    keywords: '关于日韩成人电影,站点介绍,免责声明,成人内容提示,使用说明',
  });

  return (
    <>
      <PageHeader
        eyebrow="About us"
        title="关于本站"
        description="我们做的事情很简单：把日韩影视作品的资料整理成可以被检索、被比较的条目，并且诚实地告诉你，这里能看到什么、不能看到什么。"
        crumbs={[{ label: '关于' }]}
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-sm text-primary-300" />
              收录 {movieStats.total} 部影片资料
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-article-line text-sm text-primary-300" />
              {newsList.length} 篇行业资讯
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line text-sm text-primary-300" />
              最近更新 2026.10.18
            </span>
          </>
        }
      />

      <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14">
          <div className="min-w-0 space-y-10">
            <section>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">站点定位</h2>
              <div className="mt-4 space-y-4 text-sm leading-[1.9] text-foreground-400 md:text-[15px]">
                <p>
                  日韩成人电影是一个面向成年观众的日韩影视内容索引站点。我们关注的是「介绍」而不是「播放」：一部作品讲什么故事、由谁执导、演员有谁、属于什么题材、大致多长，这些信息被整理在同一条目里，方便你在决定观看之前做判断。
                </p>
                <p>
                  站内收录的影片覆盖剧情、恋爱、悬疑、都市、时代剧与新人映画六个大类，并配有跨分类的标签体系。你可以按分类浏览，也可以从标签进入，把不同题材但气质相近的作品放在一起比较。
                </p>
                <p>
                  资讯栏目则记录行业侧的变化：片单公布、拍摄方式、声音与修复工艺、站内榜单。我们尽量用可核查的表述，不写夸张的标题。
                </p>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: '收录影片', value: movieStats.total, unit: '部' },
                  { label: '影片分类', value: categories.length, unit: '个' },
                  { label: '行业资讯', value: newsList.length, unit: '篇' },
                  { label: '覆盖地区', value: 2, unit: '地' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-800 bg-background-900 p-4">
                    <p className="text-[11px] text-foreground-600">{item.label}</p>
                    <p className="mt-2 font-label text-2xl font-bold text-foreground-50">
                      {item.value}
                      <span className="ml-1 text-xs font-medium text-foreground-500">{item.unit}</span>
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">内容范围</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-400">
                我们只整理与介绍相关的内容。以下这些事情，本站明确不做：
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { icon: 'ri-forbid-line', text: '不提供任何影片播放、在线观看或下载链接' },
                  { icon: 'ri-forbid-line', text: '不提供购买、租赁、会员或任何形式的付费内容' },
                  { icon: 'ri-forbid-line', text: '不收集身份证件、支付信息等敏感个人资料' },
                  { icon: 'ri-forbid-line', text: '不向第三方平台出售或共享用户浏览数据' },
                ].map((item) => (
                  <div
                    key={item.text}
                    className="flex items-start gap-2.5 rounded-lg border border-background-800 bg-background-900/70 px-4 py-3.5"
                  >
                    <i className={`${item.icon} mt-0.5 text-base text-primary-300`} />
                    <p className="text-xs leading-relaxed text-foreground-400">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    className="cursor-pointer rounded-full border border-background-700 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">使用说明</h2>
              <div className="mt-4 space-y-4">
                {[
                  {
                    title: '按分类浏览',
                    text: '进入任意分类页后，可以用地区、年份、片长筛选，并按最新、最热或评分排序。筛选条件变化时列表会即时刷新。',
                  },
                  {
                    title: '用标签横向串联',
                    text: '标签是跨分类的。「雨夜」「反转结局」这类标签会把不同题材的作品放在同一页，适合按气质而不是按类型找片。',
                  },
                  {
                    title: '收藏不需要登录',
                    text: '在影片详情页点击收藏后，记录会写入当前浏览器本地存储。换设备或清理浏览器数据后收藏会清空，这是预期行为。',
                  },
                  {
                    title: '搜索支持多字段匹配',
                    text: '搜索会同时匹配片名、原名、英文名、导演、演员、标签与摘要；资讯则匹配标题、栏目、摘要与作者，命中的关键词会高亮。',
                  },
                ].map((item, index) => (
                  <div key={item.title} className="rounded-lg border border-background-800 bg-background-900 p-4 md:p-5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-950 font-label text-xs font-semibold text-primary-200">
                        {index + 1}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-100">{item.title}</h3>
                    </div>
                    <p className="mt-2.5 text-xs leading-relaxed text-foreground-500">{item.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">成人内容提示与免责声明</h2>
              <div className="mt-4 rounded-lg border border-primary-900 bg-primary-950/60 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-accent-500/50 bg-accent-500/10 font-label text-xs font-bold text-accent-400">
                    18+
                  </span>
                  <div className="space-y-3 text-xs leading-[1.9] text-foreground-400">
                    <p>
                      本站内容涉及成人主题，仅面向年满 18 周岁、且所在地法律允许浏览此类内容的用户。进入本站即表示你确认自己符合上述条件。
                    </p>
                    <p>
                      站内所有影片名称、封面图像、导演与演员姓名、剧情简介、资讯内容与数据指标，均为演示用途的虚构内容，与任何真实作品、真实人物或真实机构无关。
                    </p>
                    <p>
                      本站不提供、不存储、不代理、不传播任何影视文件，也不对用户因浏览本站内容而产生的任何后果承担责任。如你对内容有异议，或希望移除某个条目，请通过下方邮箱联系我们。
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">联系我们</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-mail-line text-sm text-accent-500" />
                    内容与纠错
                  </p>
                  <p className="mt-2 text-sm text-foreground-200">{contact}</p>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-600">
                    演示用邮箱地址，暂不接收真实邮件。正式上线前请替换为你的联系邮箱。
                  </p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-time-line text-sm text-accent-500" />
                    响应时间
                  </p>
                  <p className="mt-2 text-sm text-foreground-200">工作日 10:00 - 18:00</p>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-600">
                    涉及内容更正或权利说明的邮件，我们会在两个工作日内回复。
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/news"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览影视资讯
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
                <Link
                  to="/search"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-700 px-4 py-2.5 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  搜索影片
                </Link>
              </div>
            </section>
          </div>

          <aside className="w-full">
            <div className="sticky top-24 space-y-4">
              <div className="overflow-hidden rounded-lg border border-background-800 bg-background-900">
                <div className="h-[180px] w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20quiet%20independent%20cinema%20lobby%20at%20night%20with%20deep%20red%20velvet%20curtains%2C%20warm%20amber%20wall%20lights%2C%20vintage%20film%20posters%20in%20the%20background%2C%20tasteful%20atmospheric%20editorial%20photography%2C%20soft%20grain&width=800&height=600&seq=about-cinema-hall-51&orientation=landscape"
                    alt="电影院大堂氛围示意图"
                    title="日韩成人电影 - 关于本站"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-sm font-semibold text-foreground-100">站点速览</h2>
                  <dl className="mt-4 space-y-3 text-xs">
                    <div className="flex items-center justify-between border-b border-background-800 pb-2.5">
                      <dt className="text-foreground-600">站点性质</dt>
                      <dd className="text-foreground-200">资料索引</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-background-800 pb-2.5">
                      <dt className="text-foreground-600">内容语言</dt>
                      <dd className="text-foreground-200">简体中文</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-background-800 pb-2.5">
                      <dt className="text-foreground-600">收藏方式</dt>
                      <dd className="text-foreground-200">浏览器本地</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-600">是否需要登录</dt>
                      <dd className="text-foreground-200">不需要</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="text-sm font-semibold text-foreground-100">快速入口</h2>
                <div className="mt-4 space-y-2.5">
                  {[
                    { label: '分类列表', to: '/category/drama' },
                    { label: '影视资讯', to: '/news' },
                    { label: '我的收藏', to: '/favorites' },
                    { label: '全站搜索', to: '/search' },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex cursor-pointer items-center justify-between rounded-md border border-background-800 px-3 py-2.5 text-xs text-foreground-300 transition-colors hover:border-primary-600 hover:text-accent-400"
                    >
                      {item.label}
                      <i className="ri-arrow-right-s-line text-sm" />
                    </Link>
                  ))}
                </div>
              </div>

              <p className="px-1 text-[11px] leading-relaxed text-foreground-700">
                本站为演示站点，页面中的邮箱、数据与内容均为示例，正式使用前请替换为真实信息。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}